/**
 * Whole-screen capture for the DESKTOP exam — the third recording source
 * alongside the laptop webcam and the phone.
 *
 * Two things make this awkward and shape the design:
 *
 *  1. `getDisplayMedia` REQUIRES a user gesture every time. It is acquired in the
 *     setup step (a button tap) and must then survive the navigation to the exam
 *     runner — which unmounts the setup page. So the live stream is parked in a
 *     module-level singleton, not just component state, and the runner adopts it
 *     on mount. A re-share (after the student clicks the browser's "Stop
 *     sharing") is likewise driven by a real tap on our re-share button.
 *
 *  2. The student must share their ENTIRE SCREEN, not a tab or a single window —
 *     otherwise the capture is useless for proctoring. `displaySurface` tells us
 *     which they picked; anything but `"monitor"` is rejected and re-prompted.
 *
 * This module is desktop-only. It is never imported by the phone flow.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export type ScreenShareState =
  | "idle" // never asked
  | "requesting" // picker open
  | "ready" // whole-screen share is live
  | "unfull" // shared a tab/window, not the whole screen — must re-share
  | "ended" // the student clicked the browser's "Stop sharing"
  | "denied" // dismissed the picker
  | "error";

/**
 * The result of a `request()`. `ready` means recording can proceed; every other
 * value is a reason the caller should surface and, in most cases, log.
 */
export type ScreenShareResult = ScreenShareState;

/* -- cross-route singleton ----------------------------------------------- */

let sharedStream: MediaStream | null = null;

/** The live whole-screen stream, or null if there is none / it has ended. */
export function getSharedScreenStream(): MediaStream | null {
  if (sharedStream) {
    const track = sharedStream.getVideoTracks()[0];
    if (track && track.readyState === "live") return sharedStream;
    sharedStream = null;
  }
  return sharedStream;
}

function setSharedScreenStream(stream: MediaStream | null): void {
  sharedStream = stream;
}

/* -- hook ---------------------------------------------------------------- */

export interface ScreenShareHandle {
  stream: MediaStream | null;
  state: ScreenShareState;
  /** Attach to a <video> to preview the shared screen (used in setup). */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /**
   * Acquire (or re-acquire) the whole-screen share. MUST be called from a user
   * gesture. Resolves to the resulting state so the caller can log
   * SCREEN_SHARE_NOT_FULL on `"unfull"` and gate on `"ready"`.
   */
  request: () => Promise<ScreenShareResult>;
  /** Stop the share and clear the singleton — call after submit. */
  stop: () => void;
}

export function useScreenShare(): ScreenShareHandle {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [state, setState] = useState<ScreenShareState>("idle");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const requestingRef = useRef(false);
  const watchedTrackRef = useRef<MediaStreamTrack | null>(null);

  const watchEnded = useCallback((s: MediaStream) => {
    const track = s.getVideoTracks()[0];
    if (!track || watchedTrackRef.current === track) return;
    watchedTrackRef.current = track;
    // Fires when the student clicks the browser's own "Stop sharing" control.
    track.addEventListener(
      "ended",
      () => {
        if (getSharedScreenStream() === null) setSharedScreenStream(null);
        setStream(null);
        setState("ended");
      },
      { once: true },
    );
  }, []);

  // Adopt a share acquired earlier (in setup) that is still live.
  useEffect(() => {
    const existing = getSharedScreenStream();
    if (existing) {
      setStream(existing);
      setState("ready");
      watchEnded(existing);
    }
  }, [watchEnded]);

  // Bind to the preview <video> whenever the stream changes.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream ?? null;
    if (stream) void el.play().catch(() => undefined);
  }, [stream]);

  const request = useCallback(async (): Promise<ScreenShareResult> => {
    if (requestingRef.current) return state;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setState("error");
      return "error";
    }

    requestingRef.current = true;
    setState("requesting");
    try {
      const media = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "monitor",
          // Low-data mode: cap the framerate LOW (exam content is mostly
          // static, so low fps saves the most data while keeping text crisp)
          // and only cap resolution on very large screens — text needs pixels.
          // All ideal/max, never exact, so a device that can't satisfy them
          // degrades gracefully rather than failing the share.
          frameRate: { ideal: 8, max: 12 },
          width: { max: 1600 },
        },
        audio: false,
      });

      const track = media.getVideoTracks()[0];
      const surface = track?.getSettings().displaySurface;
      // `displaySurface` is "monitor" only for a whole screen; "window" and
      // "browser" (a tab) are rejected. A browser that omits it cannot be
      // verified, so it is treated as not-full rather than trusted.
      if (surface !== "monitor") {
        media.getTracks().forEach((t) => t.stop());
        setSharedScreenStream(null);
        setStream(null);
        setState("unfull");
        return "unfull";
      }

      setSharedScreenStream(media);
      setStream(media);
      setState("ready");
      watchEnded(media);
      return "ready";
    } catch (err) {
      const name = err instanceof DOMException ? err.name : "Error";
      // Cancelling the picker rejects with NotAllowedError — that is a dismissal,
      // not a hard failure; the student can simply try again.
      setState(name === "NotAllowedError" ? "denied" : "error");
      return name === "NotAllowedError" ? "denied" : "error";
    } finally {
      requestingRef.current = false;
    }
  }, [state, watchEnded]);

  const stop = useCallback(() => {
    getSharedScreenStream(); // prune if dead
    sharedStream?.getTracks().forEach((t) => t.stop());
    setSharedScreenStream(null);
    setStream(null);
    setState("idle");
  }, []);

  return { stream, state, videoRef, request, stop };
}
